import { Fragment, FunctionComponent } from "react";
import { Box, Card, Typography } from "@mui/material";
import styles from "./cardItem.module.css";
import { Document, UserFeedback } from "../../types";
import { Categories, LabelOutputType } from "../../context/ConfigContext/types";
import CardHead from "./CardHead";
import CardTitle from "./CardTitle";
import { usePanelContext } from "../../context/PanelContext";
import ToggleCardView, { CardViewOption } from "./ToggleCardView";

import { colorGrayLight } from "../../styles/theme";
import DocumentText from "./DocumentText";
import { useDocuments } from "../../context/DocumentContext";
import { useConfig } from "../../context/ConfigContext";
import { useClientAccessContext } from "../../context/ClientAccessContext";
import {
	isTranslatable,
	useTranslatedText,
	useTranslationContext,
} from "../../context/TranslationContext";
import { getFullDocumentText } from "../../services/fetch";

interface CardItemProps {
	item: Document;
	categories: Categories;
	numericLabels: Categories;
	onFeedbackUpdate?: (userFeedback: UserFeedback) => void;
	onFeedbackDelete?: (labelId: number, documentId: number) => void;
}

export const getDocumentsRelevancy = (item) => {
	const relevantLabel = item.labels?.find((l) => l.labelName === "Relevancy");
	const relevancyValue = relevantLabel?.prediction?.value || 0;
	return +(relevancyValue * 100).toFixed(0);
};

const CardItem: FunctionComponent<CardItemProps> = ({ item }) => {
	const { addDocumentToBasket, removeDocumentFromBasket, documentsInBasket, handleUserFeedback, handleUserFeedbackDelete } =
		useDocuments();
	const { numericLabels } = useConfig();
	const { cardView, toggleCardView } = usePanelContext();
	const { selectedClient } = useClientAccessContext();
	const { isTranslateOn, toggleTranslate } = useTranslationContext();
	const { text: displayedTitle, loading: titleLoading } = useTranslatedText(
		item.id,
		item.title
	);
	const { text: displayedPreview, loading: previewLoading } = useTranslatedText(
		item.id,
		item.previewText
	);
	const relevancy = getDocumentsRelevancy(item);

	const handleCopyFullText = async () => {
		const response = await getFullDocumentText(
			item.id,
			selectedClient.clientName
		);
		if (!response?.isSuccess) {
			throw new Error("Could not fetch the full article text");
		}
		await navigator.clipboard.writeText(response.result);
	};

	const relevancyLabel = item.labels?.find(
		(i) => i.labelType === LabelOutputType.RELEVANCY
	);

	const handleRelevancyChange = (val) => {
		if (val === null) {
			handleUserFeedbackDelete(relevancyLabel.labelId, item.id);
		} else {
			const feedback: UserFeedback = {
				documentId: item.id,
				labelName: relevancyLabel.labelName,
				userId: null,
				labelValue: numericLabels["Relevancy"][0].value,
				possibleLabelValueId: numericLabels["Relevancy"][0].id,
				value: val,
			};
			handleUserFeedback(feedback);
		}
	};
	const categoryLabels = item?.labels?.filter(
		(i) => i.labelType == LabelOutputType.CATEGORY
	);
	const currentView =
		cardView?.documentId === item.id ? cardView.view : CardViewOption.SUMMARY;

	return (
		<Card
			key={item.id}
			className={styles.card}
			sx={{
				"&:hover .cardCopyButton, & .cardCopyButton:focus-visible": {
					opacity: 1,
				},
			}}
		>
			<Box>
				<CardHead
					score={relevancy}
					isInBasket={documentsInBasket.some((d) => d.id === item.id)}
					onAddToBasket={() => addDocumentToBasket(item.id)}
					selectedRelevancy={relevancyLabel?.userFeedback?.value ?? null}
					onRelevancyChange={handleRelevancyChange}
					onCopyFullText={handleCopyFullText}
					translateOn={isTranslateOn(item.id)}
					onToggleTranslate={
						isTranslatable(item.language)
							? () => toggleTranslate(item.id)
							: undefined
					}
					translateLoading={titleLoading || previewLoading}
					translateRevealOnHover
				/>
				<Box sx={{ minHeight: "389px" }}>
					<Box sx={{ marginBottom: "20px" }}>
						<CardTitle
							title={displayedTitle}
							date={item.rawCreationDatetime}
							link={item.url}
						/>
					</Box>
					{categoryLabels?.length ? (
						<Box
							sx={{
								display: "flex",
								gap: "5px",
								flexWrap: "wrap",
								marginBottom: "16px",
							}}
						>
							{categoryLabels?.map((categoryLabel) => (
								<Fragment key={categoryLabel.labelId}>
									{categoryLabel?.prediction?.labelValue && (
										<Box
											sx={{
												backgroundColor: colorGrayLight,
												paddingBlock: "5px",
												paddingInline: "10px",
												borderRadius: "12px",
												display: "flex",
											}}
										>
											<Typography
												sx={{
													whiteSpace: "nowrap",
													fontSize: "12px",
													lineHeight: "16px",
												}}
											>
												{categoryLabel.prediction.labelValue}
											</Typography>
										</Box>
									)}
								</Fragment>
							))}
						</Box>
					) : null}
					<DocumentText text={displayedPreview} />
				</Box>
			</Box>
			<Box
				sx={{
					width: "100%",
					height: "34px",
					display: "flex",
					justifyContent: "center",
					marginTop: "20px",
				}}
			>
				<ToggleCardView
					currentView={currentView}
					onViewChange={(view) => toggleCardView(item.id, view)}
				/>
			</Box>
		</Card>
	);
};

export default CardItem;
